import type { ReactNode } from 'react'
import type { Address } from 'viem'

import {
  RESTRICTED_MARKET_CHAIN_ID,
  RESTRICTED_MARKET_COUNTRIES
} from '@/config/marketGeoRestriction'

const RESTRICTED_COUNTRY_CODES = new Set(
  RESTRICTED_MARKET_COUNTRIES.map(country => country.trim().toUpperCase())
)

export type MarketGeoRestrictionProbe =
  | { status: 'pending' }
  | { status: 'error' }
  | { status: 'success'; hasRestrictedAsset: boolean }

export interface MarketGeoRestrictionVerdict {
  isRestricted: boolean
  isPending: boolean
}

export interface MarketGeoRestrictionActionProps {
  isFinalizeDisabled: boolean
  isLoading: boolean
  finalizeLabel: ReactNode
  tooltipContent?: ReactNode
  shouldBlockFinalize: boolean
}

// Values that carry no location: absent, malformed, or the conventional unknown sentinels. Both
// platforms omit the header instead of sending a sentinel, so this only guards a proxy regression.
const UNKNOWN_COUNTRY_SENTINELS = new Set(['XX', 'ZZ'])

const isResolvedCountry = (countryCode: string | null | undefined) => {
  const normalized = countryCode?.trim().toUpperCase()
  return !!normalized && /^[A-Z]{2}$/.test(normalized) && !UNKNOWN_COUNTRY_SENTINELS.has(normalized)
}

/**
 * Returns whether the market-action asset probe should run. An unresolved country probes like a
 * restricted one, so a viewer the edge could not locate is blocked on stock markets rather than
 * waved through; probing rather than blocking outright keeps other Robinhood markets usable.
 */
export const shouldProbeMarketGeoRestriction = (
  countryCode: string | null | undefined,
  chainId: number | undefined
) =>
  (!isResolvedCountry(countryCode) || isRestrictedMarketCountry(countryCode)) &&
  chainId === RESTRICTED_MARKET_CHAIN_ID

/**
 * Derives ActionButtons props and the imperative finalize guard from a geo verdict.
 *
 * Pending blocks the action without labeling it unavailable; restricted suppresses the tooltip.
 */
export const getMarketGeoRestrictionActionProps = ({
  geoRestriction,
  isFinalizeDisabled,
  isLoading,
  finalizeLabel,
  tooltipContent
}: {
  geoRestriction: MarketGeoRestrictionVerdict & { message: string }
  isFinalizeDisabled: boolean
  isLoading: boolean
  finalizeLabel: ReactNode
  tooltipContent?: ReactNode
}): MarketGeoRestrictionActionProps => {
  const shouldBlockFinalize = geoRestriction.isRestricted || geoRestriction.isPending

  return {
    isFinalizeDisabled: isFinalizeDisabled || shouldBlockFinalize,
    isLoading: geoRestriction.isRestricted ? false : isLoading || geoRestriction.isPending,
    finalizeLabel: geoRestriction.isRestricted ? geoRestriction.message : finalizeLabel,
    tooltipContent: geoRestriction.isRestricted ? undefined : tooltipContent,
    shouldBlockFinalize
  }
}

export const deduplicateAddresses = (
  addresses: readonly (Address | null | undefined)[]
): Address[] =>
  Array.from(
    new Map(
      addresses
        .filter((address): address is Address => !!address)
        .map(address => [address.toLowerCase(), address] as const)
    ).values()
  )

export const isRestrictedMarketCountry = (countryCode: string | null | undefined) =>
  !!countryCode && RESTRICTED_COUNTRY_CODES.has(countryCode.trim().toUpperCase())

/**
 * Resolves the four policy branches: out-of-scope country/chain is unrestricted; pending blocks
 * without labeling; probe errors fail closed; and a successful probe follows its asset verdict.
 */
export const getMarketGeoRestrictionVerdict = (
  countryCode: string | null | undefined,
  chainId: number | undefined,
  probe: MarketGeoRestrictionProbe
): MarketGeoRestrictionVerdict => {
  if (!shouldProbeMarketGeoRestriction(countryCode, chainId)) {
    return { isRestricted: false, isPending: false }
  }

  if (probe.status === 'pending') return { isRestricted: false, isPending: true }
  if (probe.status === 'error') return { isRestricted: true, isPending: false }

  return { isRestricted: probe.hasRestrictedAsset, isPending: false }
}
